import type { PageWidgetLike, Resource } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { PageWidgetType } from '@efdigital/ll-addon-data/types/content/base/base-page-widget';
import type { Store } from '@warp-drive/core';

/**
 * Count words in a markdown string.
 * Strips markdown syntax and counts actual words.
 *
 * @param markdown - The markdown string to count words in
 * @returns The number of words in the markdown content
 */
export function countWordsInMarkdown(markdown: string): number {
  if (!markdown || markdown.trim() === '') {
    return 0;
  }

  // Remove markdown syntax: headers, links, images, code blocks, etc.
  const text = markdown
    .replace(/```[\s\S]*?```/g, '') // Code blocks
    .replace(/`[^`]*`/g, '') // Inline code
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // Links [text](url) -> text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // Images ![alt](url) -> alt
    .replace(/[!#*_~`[\]()]/g, '') // Markdown punctuation (including '!' from images)
    .replace(/\n+/g, ' ') // Newlines to spaces
    .trim();

  // Split by whitespace and filter empty strings
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  return words.length;
}

/**
 * Calculate reading time from word count.
 * Uses average reading speed of 200 words per minute.
 *
 * @param wordCount - The number of words
 * @returns Reading time in minutes (rounded up)
 */
export function calculateReadingTime(wordCount: number): number {
  // Average reading speed: 200 words per minute
  return Math.ceil(wordCount / 200);
}

/**
 * Get duration from resources referenced in a resource widget.
 * Only includes resources that are already loaded in the store.
 *
 * @param resourceIds - Array of resource IDs to look up
 * @param store - The Warp-Drive store instance
 * @returns Total duration in minutes from all found resources
 */
export function getResourceDurations(
  resourceIds: string[],
  store: Store
): number {
  let totalMinutes = 0;

  for (const resourceId of resourceIds) {
    if (!resourceId) {
      continue;
    }

    // Try to peek the resource from the store (already loaded)
    const resource = store.peekRecord<Resource>(
      DataTypeContent.Resource,
      resourceId
    );

    if (resource && resource.duration != null) {
      totalMinutes += resource.duration;
    }
  }

  return totalMinutes;
}

/**
 * Estimate page duration based on widgets.
 * - Markdown widgets: calculates reading time (200 words per minute)
 * - Resource widgets: sums duration from resources (if loaded in store)
 *
 * @param widgets - Array of page widgets to analyze
 * @param store - The Warp-Drive store instance
 * @returns Estimated duration in minutes, or 0 if no content found
 */
export function estimatePageDuration(
  widgets: PageWidgetLike[],
  store: Store
): number {
  if (!widgets || widgets.length === 0) {
    return 0;
  }

  let totalMinutes = 0;

  for (const widget of widgets) {
    // Handle markdown widgets
    if (widget.widgetType === PageWidgetType.Markdown) {
      const markdownWidget = widget;
      const wordCount = countWordsInMarkdown(markdownWidget.markdown || '');
      const readingTime = calculateReadingTime(wordCount);
      totalMinutes += readingTime;
    }

    // Handle resource widgets
    if (widget.widgetType === PageWidgetType.Resource) {
      const resourceWidget = widget;
      const resourceIds = resourceWidget.resourceIds || [];
      const resourceDuration = getResourceDurations(resourceIds, store);
      totalMinutes += resourceDuration;
    }
  }

  return totalMinutes;
}

/**
 * Round a duration to the nearest N minutes.
 *
 * @param duration - The duration to round
 * @param nearest - The nearest number of minutes to round to
 * @returns The rounded duration
 */
export function roundToNearest(duration: number, nearest: number): number {
  return Math.round(duration / nearest) * nearest;
}
